import { OrganizationComment } from '../../../services/organizations/organization.model';
import { BehaviorSubject } from 'rxjs';
import fewComments from './few.json';
import manyComments from './many.json';

export type CommentMockMode = 'empty' | 'few' | 'many';

const STORAGE_KEY = 'htx-mock-comments-mode';
const STORAGE_KEY_COMMENTS = 'htx-mock-comments-data';

function getInitialMode(): CommentMockMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'empty' || stored === 'few' || stored === 'many') {
    return stored;
  }
  return 'few';
}

const currentMode$ = new BehaviorSubject<CommentMockMode>(getInitialMode());
let additionalComments: OrganizationComment[] = [];

// Load additional comments from localStorage
function loadAdditionalComments(): void {
  const stored = localStorage.getItem(STORAGE_KEY_COMMENTS);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      additionalComments = parsed.map((c: OrganizationComment) => ({
        ...c,
        date: new Date(c.date)
      }));
    } catch (e) {
      console.error('Failed to load additional comments:', e);
      additionalComments = [];
    }
  }
}

// Save additional comments to localStorage
function saveAdditionalComments(): void {
  localStorage.setItem(STORAGE_KEY_COMMENTS, JSON.stringify(additionalComments));
}

// Initialize
loadAdditionalComments();

export function loadEmptyComments(): OrganizationComment[] {
  return [];
}

export function loadFewComments(): OrganizationComment[] {
  return fewComments.map(c => ({
    ...c,
    date: new Date(c.date).toISOString()
  })) as OrganizationComment[];
}

export function loadManyComments(): OrganizationComment[] {
  return manyComments.map(c => ({
    ...c,
    date: new Date(c.date).toISOString()
  })) as OrganizationComment[];
}

export function setCommentMockMode(mode: CommentMockMode): void {
  localStorage.setItem(STORAGE_KEY, mode);
  currentMode$.next(mode);
}

export function getCommentMockMode$() {
  return currentMode$.asObservable();
}

export function getCurrentCommentMockMode(): CommentMockMode {
  return currentMode$.value;
}

export function addComment(comment: OrganizationComment): void {
  additionalComments.push(comment);
  saveAdditionalComments();
}

export function clearAdditionalComments(): void {
  additionalComments = [];
  localStorage.removeItem(STORAGE_KEY_COMMENTS);
}

export function hasAdditionalComments(): boolean {
  return additionalComments.length > 0;
}

export function getComments(organizationId?: string): OrganizationComment[] {
  const mode = currentMode$.value;
  let baseComments: OrganizationComment[];

  switch (mode) {
    case 'empty':
      baseComments = loadEmptyComments();
      break;
    case 'few':
      baseComments = loadFewComments();
      break;
    case 'many':
      baseComments = loadManyComments();
      break;
    default:
      baseComments = loadFewComments();
  }

  // Merge base comments with additional comments, sort by date (newest first)
  let allComments = [...baseComments, ...additionalComments].sort((a, b) =>
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  // Filter by organizationId if provided
  if (organizationId) {
    const filtered = allComments.filter(c =>
      (c as any).organizationId === organizationId
    );
    // Fallback: if no comments match the org ID, return all (mock flexibility)
    if (filtered.length > 0) {
      allComments = filtered;
    }
  }

  return allComments;
}
